import { catchError, from, map, Observable, switchMap, tap } from 'rxjs';
import { AxiosError, AxiosResponse } from 'axios';
import { UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById } from '@ngneat/elf-entities';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';
import fileDownload from 'js-file-download';
import { GridSortItem } from '@mui/x-data-grid-pro';

import { suppliersStore } from '@store/suppliers/suppliers.store';
import { SuppliersFilters, SuppliersPagination } from '@store/suppliers/suppliers.model';
import { getSuppliersDataSource } from '@store/suppliers/suppliers.requests';
import { Pagination } from '@store/common/pagination.model';
import { ManageOrganisation } from '@store/common/organisation.model';
import { GlobalStatus } from '@store/common/globalStatus.enum';
import { TableFilter } from '@store/common/tableFilter.model';

import { TableConfig } from '@constants/table.constants';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';
import { OrganisationStatus } from '@store/common/organisation.enums';

export class SuppliersService {
  store = suppliersStore;

  setStatus = (status?: GlobalStatus) => this.store.update((state) => ({ ...state, status }));
  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: { search: '', tableFilters: SuppliersFilters.initTableFilters() },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  searchSuppliers = (search?: string): Observable<SuppliersPagination> => {
    return from(APIAxios({
      ...APIRoutes.GETSuppliers(),
      params: {
        searchString: search || undefined,
        sortBy: 'organisation.name',
        orderBy: 'ASC',
        page: 1,
        limit: TableConfig.pagination,
        status: OrganisationStatus.ACTIVE
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SuppliersPagination>) => {
          return response.data;
        }),
      );
  };

  getSuppliers = (): Observable<SuppliersPagination> => {
    const { status, filters: { search, tableFilters }, sort, tablePagination } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETSuppliers(),
      params: {
        ...requestFilters,
        status,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SuppliersPagination>) => {
          return response.data;
        }),
        tap((suppliersPagination) => {
          const paginationData = Pagination.toPaginationData(suppliersPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(suppliersPagination.data),
            upsertEntitiesById(suppliersPagination.data.map((supplier) => supplier.id), {
              updater: (supplierUi) => supplierUi,
              creator: (supplierId) => ({ id: supplierId, selected: false }),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, suppliersPagination.data.map((supplier) => supplier.id)),
            getSuppliersDataSource.setSuccess(),
          );
        }),
        getSuppliersDataSource.trackRequestStatus(),
      );
  };

  getSuppliersCSV = (): Observable<Blob> => {
    const { status, filters: { search, tableFilters }, sort } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETSuppliersCSV(),
      params: {
        ...requestFilters,
        disablePaginate: true,
        status,
        searchString: search || undefined,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Blob>) => {
          return response.data;
        }),
        tap((data) => {
          const date = new Date();
          const formattedDate =
            date.getDate() + '_' + (date.getMonth() + 1) + '_' + date.getFullYear();
          fileDownload(data, `supplier_organisations_${formattedDate}.csv`);
        }),
      );
  };

  updateSupplierStatusInList = (supplierId: number | string, status: GlobalStatus): Observable<SuppliersPagination> => {
    return from(APIAxios({
      ...APIRoutes.PATCHSupplierStatus(supplierId),
      data: { status },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSuppliers()),
      );
  };

  createSupplier = (data: ManageOrganisation): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.POSTSupplier(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisation>) => {
          return response.data;
        }),
      );
  };

  deleteSupplierInList = (supplierId: number | string): Observable<SuppliersPagination> => {
    return from(APIAxios(APIRoutes.DELETESupplier(supplierId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSuppliers()),
      );
  };

  setSelectedSuppliers = (supplierIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (supplierUI) => ({ ...supplierUI, selected: supplierIds.includes(supplierUI.id) }),
        { ref: UIEntitiesRef },
      ),
    );
  };
}

export const suppliersService = new SuppliersService();
